import { ChatSDKError } from '@/lib/errors';
import { FetchError } from '@/lib/utils';

/**
 * Decides whether an error is worth a `$exception` in error tracking.
 *
 * Report by default: a missed error is invisible, a noisy one can be silenced
 * later. Only errors that are an expected answer to the user are skipped:
 * deliberate 4xx responses (validation, auth, not found, limits),
 * cancellations and Next.js control flow. Classify by type, status or digest,
 * never by message text.
 */
export function isReportableError(error: unknown): boolean {
  if (isAbortError(error)) return false;
  if (isNextControlFlowError(error)) return false;

  if (error instanceof ChatSDKError) {
    // The user's own connection dropped; nothing on our side to fix.
    if (error.type === 'offline') return false;
    return error.statusCode >= 500;
  }

  // Our own API already answered a 4xx deliberately. Provider errors stay
  // reportable whatever their status: a 400/401/429 from a model provider is
  // our bug or an outage, not a user mistake.
  if (error instanceof FetchError && error.status < 500) return false;

  return true;
}

/**
 * Browser-only exclusions on top of `isReportableError`. The browser cannot
 * tell an outage from its own connection, and a 5xx from our API was already
 * reported by the server that produced it; reporting it again from every tab
 * doubles each incident.
 */
export function isReportableClientError(error: unknown): boolean {
  if (!isReportableError(error)) return false;
  if (isBrowserNetworkFailure(error)) return false;
  // `throwOnFetchError` wraps our own API's answers; a 5xx there is the
  // server's error, reported where it happened.
  if (error instanceof FetchError) return false;
  return true;
}

// What browsers throw when the request never got an answer: the laptop is
// offline or waking up, the tab is navigating away, a proxy cut the socket.
// The only signal is the message, because the browser's TypeError has no
// code; these are the exact strings Chromium, Firefox and WebKit use.
const BROWSER_NETWORK_FAILURE_MESSAGES = new Set([
  'failed to fetch',
  'load failed',
  'network request failed',
  'networkerror when attempting to fetch resource.',
  'the network connection was lost.',
  'the internet connection appears to be offline.',
]);

export function isBrowserNetworkFailure(error: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return true;
  }
  if (!(error instanceof TypeError)) return false;
  return BROWSER_NETWORK_FAILURE_MESSAGES.has(
    error.message.trim().toLowerCase(),
  );
}

// Digests of the errors Next.js throws for control flow (redirect, notFound,
// dynamic-rendering bail-outs during prerender): what `unstable_rethrow`
// rethrows. Checked by digest so this stays free of `next` imports.
const NEXT_CONTROL_FLOW_DIGESTS = new Set([
  'NEXT_REDIRECT',
  'NEXT_HTTP_ERROR_FALLBACK',
  'DYNAMIC_SERVER_USAGE',
  'BAILOUT_TO_CLIENT_SIDE_RENDERING',
  'HANGING_PROMISE_REJECTION',
  'NEXT_PRERENDER_INTERRUPTED',
]);

function isNextControlFlowError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  if ('digest' in error && typeof error.digest === 'string') {
    const [code] = error.digest.split(';');
    if (NEXT_CONTROL_FLOW_DIGESTS.has(code)) return true;
  }
  // Like `unstable_rethrow`, look through wrappers such as `{ cause }`.
  return 'cause' in error && isNextControlFlowError(error.cause);
}

function isAbortError(error: unknown): boolean {
  return (
    !!error &&
    typeof error === 'object' &&
    'name' in error &&
    (error.name === 'AbortError' || error.name === 'ResponseAborted')
  );
}
